import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { convertHighlightToHighlightInput } from '../../../utils/inputTransform';
import { createHighlight as createHighlightMutation } from '../apiMutations';
import { getGameAirings } from '../../games/apiQueries';
import { getLatestHighlight } from '../../airings/apiQueries';
import { build } from './highlightBuilder';
import { Airing, Highlight, Territory } from '../../../utils/types';

export default function useHighlightSave(isMultiSave?: boolean): SaveFnType {
	const [createHighlight] = useMutation(createHighlightMutation);
	const apolloClient = useApolloClient();

	const save: SaveFnType = async (highlight: Highlight): Promise<SaveFnResponseType> => {
		await createHighlight({
			variables: {
				highlight: convertHighlightToHighlightInput(highlight)
			}
		});
		return Promise.resolve({
			success: true,
			message: 'New Highlight created'
		});
	};

	const getAiringsForGame = async (gameId: string): Promise<Airing[] | []> => {
		try {
			const { data } = await apolloClient.query({
				query: getGameAirings,
				variables: {
					id: decodeURIComponent(gameId)
				},
				fetchPolicy: 'no-cache'
			});

			return data?.getGame?.airings?.items || [];
		} catch (e) {
			throw new Error('Could not find Airings');
		}
	};

	const getLatestHighlightForAiring = async (airingId: string): Promise<Highlight | null> => {
		try {
			const { data } = await apolloClient.query({
				query: getLatestHighlight,
				variables: { airingId: decodeURIComponent(airingId), type: 'DRAFT' },
				fetchPolicy: 'no-cache'
			});

			return data?.getLatestHighlight || null;
		} catch (e) {
			throw new Error('Could not find Sibling Highlight');
		}
	};

	const saveHighlightForAiring = async (airing: Airing, sourceHighlight: Highlight): Promise<boolean> => {
		const siblingHighlight = await getLatestHighlightForAiring(airing.id);
		if (!siblingHighlight) {
			throw new Error('Could not find sibling Highlight');
		} else {
			const newHighlight = build(airing, sourceHighlight, siblingHighlight);

			if (!newHighlight?.periods?.items?.length) {
				throw new Error('Composed Sibling Highlight has no Periods');
			}
			return (await save(newHighlight)).success;
		}
	};

	const multiSave: SaveFnType = async (highlight: Highlight): Promise<SaveFnResponseType> => {
		const isFirstHighlightSaved = await save(highlight);

		if (isFirstHighlightSaved) {
			if (!highlight?.gameId) {
				throw new Error('Source Highlight is missing gameId');
			} else {
				const allAirings = await getAiringsForGame(highlight?.gameId);
				const siblingAirings = allAirings.filter((airing: Airing) => airing.id !== highlight?.airingId);

				const promises: Promise<boolean>[] = [];
				siblingAirings.forEach((airing: Airing) => {
					promises.push(saveHighlightForAiring(airing, highlight));
				});

				const result = await Promise.all(promises);
				const allSuccessful = !result.includes(false);
				return Promise.resolve({
					success: allSuccessful,
					message: constructMessage(allAirings)
				});
			}
		}

		return Promise.resolve({
			success: false,
			message: 'Something went wrong'
		});
	};

	return isMultiSave ? multiSave : save;
}

const constructMessage = (airings: Airing[]): string => {
	const territories: (Territory | undefined)[] = airings
		.filter((airing: Airing) => airing?.channel?.territory)
		.map((airing): Territory | undefined => airing?.channel?.territory || undefined);

	return `New Highlights created (territories: ${territories.join(', ')})`;
};

type SaveFnResponseType = {
	success: boolean;
	message: string;
};

type SaveFnType = (highlight: Highlight) => Promise<SaveFnResponseType>;
